/**
 * Registro seduta (V67), la scheda Registro seduta: minuti e RPE eseguiti per
 * giocatore × blocco, un gruppo alla volta nel componente Tabs, ogni cella su
 * una riga con la lettura a icona e il motivo. Le righe nascono chiudendo
 * l'appello.
 * Il salvataggio è per cella, ritentato in rete. In linea, Sotto e Sopra
 * confrontano le UA; il motivo si chiede solo se minuti o RPE differiscono
 * dallo snapshot.
 */

import { useRef, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { dataLocale, puo } from '@calcio/shared';
import { toast } from 'sonner';
import {
  ActivityIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  CircleAlertIcon,
  ClockIcon,
  EqualIcon,
  GaugeIcon,
  InfoIcon,
  type LucideIcon,
  MessageSquarePlusIcon,
  MessageSquareTextIcon,
  TriangleAlertIcon,
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { chiama } from '../api/client.js';
import type { CodaRighe, EsitoRiga } from '../comuni/coda.js';
import { messaggio, useDati } from '../comuni/dati.js';
import { Maglia } from '../comuni/Maglia.js';
import { SIGLA_RUOLO } from '../giocatori/elenchi.js';
import { StatoCaricamento } from '../comuni/Testata.js';
import { useCodaRighe } from '../comuni/useCodaRighe.js';
import { useContesto } from '../navigazione/contesto.js';
import { NonTrovata } from '../navigazione/NonTrovata.js';
import {
  type BloccoLog,
  ETICHETTA_LETTURA,
  type GiocatoreLog,
  type GruppoLog,
  type LogSeduta as DatiLog,
  type RigaLog,
  celleDaLog,
  chiaveLog,
  daValoreLog,
  nomeInRosa,
  rigaLogDi,
  valoreLog,
} from './allenamenti.js';
import { BarraScheda, StatoSalvataggio } from '../comuni/Riquadri.js';
import { StatoVuoto } from '../comuni/StatoVuoto.js';

const LETTURE = ['in_linea', 'sotto', 'sopra'] as const;

/** La lettura della cella con i colori fissi degli stati: verde, giallo, rosso. */
const COLORE_LETTURA: Record<RigaLog['lettura'], string> = {
  in_linea: 'bg-stato-disponibile text-white',
  sotto: 'bg-stato-affaticato text-black',
  sopra: 'bg-stato-infortunato text-white',
};

const ICONA_LETTURA: Record<RigaLog['lettura'], LucideIcon> = {
  in_linea: EqualIcon,
  sotto: ArrowDownIcon,
  sopra: ArrowUpIcon,
};

type MetaCella = {
  lettura: RigaLog['lettura'];
  motivo: string | null;
  motivoVisibile: boolean;
};

function metaDa(dati: DatiLog): Record<string, MetaCella> {
  const m: Record<string, MetaCella> = {};
  for (const g of dati.gruppi) {
    for (const j of g.giocatori) {
      for (const b of g.blocchi) {
        const r = rigaLogDi(b, j.id);
        if (!r) continue;
        m[chiaveLog(b.bloccoId, j.id)] = {
          lettura: r.lettura,
          motivo: r.motivo,
          motivoVisibile: r.motivoVisibile,
        };
      }
    }
  }
  return m;
}

export function LogSeduta() {
  const { ss, id } = useParams<{ ss: string; id: string }>();
  const { utente } = useContesto();
  const legge = Boolean(ss && puo(utente.assegnazioni, 'allenamento.leggi', ss));
  const { dati, errore } = useDati<DatiLog>(legge ? `/allenamenti/${id}/registro` : '');

  if (!ss || !legge) return <NonTrovata />;
  if (errore) return <StatoCaricamento errore={errore} />;
  if (!dati || dati.allenamento.id !== id) return <StatoCaricamento errore={null} />;
  if (dati.allenamento.squadraStagioneId !== ss) {
    return (
      <Navigate
        to={`/sq/${dati.allenamento.squadraStagioneId}/allenamenti/${id}/registro`}
        replace
      />
    );
  }
  return (
    <Griglia
      key={id}
      dati={dati}
      puoScrivere={puo(utente.assegnazioni, 'allenamento.gestisci', ss)}
    />
  );
}

function Griglia({ dati, puoScrivere }: { dati: DatiLog; puoScrivere: boolean }) {
  const a = dati.allenamento;
  const arrivato = dataLocale(a.dataOra) <= dataLocale(new Date());
  const scrive = puoScrivere && a.stato !== 'annullato' && arrivato;
  const [motivoAperto, setMotivoAperto] = useState<string | null>(null);
  const [gruppo, setGruppo] = useState(() => dati.gruppi[0]?.id ?? '');
  const [meta, setMeta] = useState(() => metaDa(dati));
  const metaRef = useRef(meta);
  metaRef.current = meta;

  const c = useCodaRighe<string>(
    () => celleDaLog(dati.gruppi),
    async (chiave, voluto) => {
      const [bloccoId, giocatoreId] = chiave.split(':');
      if (!voluto) {
        await chiama(`/allenamenti/${a.id}/blocchi/${bloccoId}/registro/${giocatoreId}`, {
          metodo: 'DELETE',
        });
        setMeta((m) => {
          const prossimo = { ...m };
          delete prossimo[chiave];
          return prossimo;
        });
        return;
      }
      const letto = daValoreLog(voluto);
      if (!letto) return;
      const riga = await chiama<RigaLog>(
        `/allenamenti/${a.id}/blocchi/${bloccoId}/registro/${giocatoreId}`,
        {
          metodo: 'PUT',
          corpo: {
            minutiEseguiti: letto.minuti,
            rpeEseguito: letto.rpe,
            motivo: metaRef.current[chiave]?.motivo ?? undefined,
          },
        },
      );
      setMeta((m) => ({
        ...m,
        [chiave]: {
          lettura: riga.lettura,
          motivo: riga.motivo,
          motivoVisibile: riga.motivoVisibile,
        },
      }));
    },
    (rifiuto) => toast.error(rifiuto),
  );

  const inSospeso = c.inSospeso();
  const nonSalvate = Object.keys(celleDaLog(dati.gruppi)).filter(
    (k) => c.esito(k) === 'non_salvato',
  ).length;

  const salvaCella = (chiave: string, voluto: string | null) => {
    if (!scrive) return;
    if (c.valore(chiave) === voluto) return;
    c.imposta(chiave, voluto);
  };

  const salvaMotivo = async (chiave: string, motivo: string) => {
    const [bloccoId, giocatoreId] = chiave.split(':');
    const letto = daValoreLog(c.valore(chiave));
    if (!letto || !bloccoId || !giocatoreId) return;
    const testo = motivo.trim() || null;
    try {
      const riga = await chiama<RigaLog>(
        `/allenamenti/${a.id}/blocchi/${bloccoId}/registro/${giocatoreId}`,
        {
          metodo: 'PUT',
          corpo: {
            minutiEseguiti: letto.minuti,
            rpeEseguito: letto.rpe,
            motivo: testo ?? undefined,
          },
        },
      );
      setMeta((m) => ({
        ...m,
        [chiave]: {
          lettura: riga.lettura,
          motivo: riga.motivo,
          motivoVisibile: riga.motivoVisibile,
        },
      }));
      setMotivoAperto(null);
    } catch (e) {
      toast.error(messaggio(e));
    }
  };

  const letture = Object.values(meta).reduce(
    (n, m) => ({ ...n, [m.lettura]: (n[m.lettura] ?? 0) + 1 }),
    {} as Partial<Record<RigaLog['lettura'], number>>,
  );

  return (
    <>
      <BarraScheda>
        {LETTURE.map((l) => {
          const Icona = ICONA_LETTURA[l];
          return (
            <span key={l} className="flex items-center gap-1 text-muted-foreground">
              <span
                className={cn('flex size-5 items-center justify-center rounded', COLORE_LETTURA[l])}
              >
                <Icona aria-hidden className="size-3.5" />
              </span>
              <span className="font-semibold text-foreground tabular-nums">{letture[l] ?? 0}</span>
              {ETICHETTA_LETTURA[l].toLowerCase()}
            </span>
          );
        })}
        {scrive && (
          <StatoSalvataggio
            inSospeso={inSospeso}
            nonSalvate={nonSalvate}
            onRiprova={() => c.riprova()}
          />
        )}
      </BarraScheda>

      {!dati.contaNeiCalcoli && (
        <Alert>
          <InfoIcon />
          <AlertTitle>Allenamento annullato</AlertTitle>
          <AlertDescription>
            Il Registro seduta resta visibile ma non conta nei calcoli.
          </AlertDescription>
        </Alert>
      )}
      {a.stato !== 'annullato' && !arrivato && (
        <Alert>
          <InfoIcon />
          <AlertTitle>Registro non ancora aperto</AlertTitle>
          <AlertDescription>
            Le righe nascono chiudendo l'appello, allineate al piano, e si correggono dal giorno
            della seduta.
          </AlertDescription>
        </Alert>
      )}

      {dati.gruppi.length === 0 ? (
        <StatoVuoto titolo="Nessun gruppo" descrizione="Componi prima la seduta." />
      ) : (
        <Tabs value={gruppo} onValueChange={setGruppo}>
          <TabsList className="max-w-full justify-start overflow-x-auto [scrollbar-width:none]">
            {dati.gruppi.map((g) => (
              <TabsTrigger key={g.id} value={g.id} title={g.nome}>
                {g.blocchi.some((b) => b.classifica !== 'nessuno') && (
                  <TriangleAlertIcon className="text-stato-recupero" />
                )}
                {/* I gruppi di sistema con la sigla del ruolo, i custom con il loro nome. */}
                {g.diSistema && g.ruolo ? SIGLA_RUOLO[g.ruolo] : g.nome}
                <Badge variant="secondary" className="tabular-nums">
                  {g.giocatori.length}
                </Badge>
              </TabsTrigger>
            ))}
          </TabsList>
          {dati.gruppi.map((g) => (
            <TabsContent key={g.id} value={g.id} className="flex flex-col gap-3 pt-2">
              <Gruppo
                gruppo={g}
                scrive={scrive}
                coda={c}
                meta={meta}
                onSalva={salvaCella}
                onMotivo={(chiave) => setMotivoAperto(chiave)}
              />
            </TabsContent>
          ))}
        </Tabs>
      )}

      {motivoAperto && (
        <DialogoMotivo
          aperto
          motivo={meta[motivoAperto]?.motivo ?? ''}
          onChiudi={() => setMotivoAperto(null)}
          onSalva={(testo) => void salvaMotivo(motivoAperto, testo)}
        />
      )}
    </>
  );
}

function Gruppo({
  gruppo,
  scrive,
  coda,
  meta,
  onSalva,
  onMotivo,
}: {
  gruppo: GruppoLog;
  scrive: boolean;
  coda: CodaRighe<string>;
  meta: Record<string, MetaCella>;
  onSalva: (chiave: string, voluto: string | null) => void;
  onMotivo: (chiave: string) => void;
}) {
  if (gruppo.blocchi.length === 0) {
    return <StatoVuoto titolo="Nessun blocco in questo gruppo" />;
  }
  if (gruppo.giocatori.length === 0) {
    return <StatoVuoto titolo="Nessun giocatore in questo gruppo" />;
  }
  return (
    <>
      <AvvisoClassifica blocchi={gruppo.blocchi} giocatori={gruppo.giocatori} />
      <div className="rounded-lg border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="sticky left-0 z-[1] bg-card">Giocatore</TableHead>
              {gruppo.blocchi.map((b) => (
                <TableHead key={b.bloccoId} className="h-auto py-2">
                  <span className="flex items-center gap-1">
                    {b.classifica !== 'nessuno' && (
                      <TriangleAlertIcon
                        aria-label="Blocco segnalato"
                        className="size-4 shrink-0 text-stato-recupero"
                      />
                    )}
                    <span className="truncate">{b.nome}</span>
                  </span>
                  <span className="flex items-center gap-2 text-[13px] font-normal text-muted-foreground tabular-nums">
                    <span className="flex items-center gap-0.5" title="Minuti programmati">
                      <ClockIcon aria-hidden className="size-3.5" />
                      {b.minutiProgrammati}′
                    </span>
                    <span className="flex items-center gap-0.5" title="RPE target">
                      <GaugeIcon aria-hidden className="size-3.5" />
                      {b.rpeTarget}
                    </span>
                  </span>
                </TableHead>
              ))}
              {/* Prende lo spazio che avanza: i blocchi restano a sinistra, accanto ai nomi. */}
              <TableHead aria-hidden className="w-full" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {gruppo.giocatori.map((j) => (
              <TableRow key={j.id}>
                <TableCell className="sticky left-0 z-[1] bg-card">
                  <div className="flex items-center gap-2">
                    <Maglia numero={j.numero} ruolo={j.ruoloPreferito} />
                    <div className="flex min-w-0 flex-col">
                      <span className="font-semibold">
                        {j.cognome} {j.nome.charAt(0)}.
                      </span>
                      <span
                        className="flex items-center gap-1 text-[13px] text-muted-foreground tabular-nums"
                        title="Dose della seduta: minuti × RPE su tutti i blocchi"
                      >
                        <ActivityIcon aria-hidden className="size-3.5" />
                        {j.doseTotaleUa} UA
                      </span>
                    </div>
                  </div>
                </TableCell>
                {gruppo.blocchi.map((b) => {
                  const chiave = chiaveLog(b.bloccoId, j.id);
                  return (
                    <TableCell key={b.bloccoId}>
                      <Cella
                        nome={nomeInRosa(j)}
                        blocco={b.nome}
                        valore={coda.valore(chiave)}
                        esito={coda.esito(chiave)}
                        meta={meta[chiave]}
                        scrive={scrive}
                        onSalva={(voluto) => onSalva(chiave, voluto)}
                        onMotivo={() => onMotivo(chiave)}
                      />
                    </TableCell>
                  );
                })}
                <TableCell aria-hidden />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

/** I blocchi segnalati del gruppo in un solo box: da ricalibrare o da gestire per giocatore. */
function AvvisoClassifica({
  blocchi,
  giocatori,
}: {
  blocchi: BloccoLog[];
  giocatori: GiocatoreLog[];
}) {
  const segnalati = blocchi.filter((b) => b.classifica !== 'nessuno');
  if (segnalati.length === 0) return null;
  return (
    <Alert>
      <TriangleAlertIcon />
      <AlertTitle>
        {segnalati.length === 1
          ? 'Un blocco fuori dal piano'
          : `${segnalati.length} blocchi fuori dal piano`}
      </AlertTitle>
      <AlertDescription>
        <ul className="flex flex-col gap-0.5">
          {segnalati.map((b) => {
            const nomi = b.giocatoriAlert
              .map((id) => giocatori.find((j) => j.id === id))
              .filter((j): j is GiocatoreLog => Boolean(j))
              .map(nomeInRosa);
            return (
              <li key={b.bloccoId}>
                <span className="font-medium text-foreground">{b.nome}</span>:{' '}
                {b.classifica === 'sistemico'
                  ? 'riferimento della scheda da ricalibrare.'
                  : `gestione individuale${nomi.length ? ` (${nomi.join(', ')})` : ''}.`}
              </li>
            );
          })}
        </ul>
      </AlertDescription>
    </Alert>
  );
}

function Cella({
  nome,
  blocco,
  valore,
  esito,
  meta,
  scrive,
  onSalva,
  onMotivo,
}: {
  nome: string;
  blocco: string;
  valore: string | null;
  esito: EsitoRiga;
  meta: MetaCella | undefined;
  scrive: boolean;
  onSalva: (voluto: string | null) => void;
  onMotivo: () => void;
}) {
  const letto = daValoreLog(valore);
  const [minuti, setMinuti] = useState(letto ? String(letto.minuti) : '');
  const [rpe, setRpe] = useState(letto ? String(letto.rpe) : '');

  const conferma = () => {
    const m = minuti.trim();
    const r = rpe.trim();
    if (m === '' && r === '') {
      onSalva(null);
      return;
    }
    const minutiN = Number(m);
    const rpeN = Number(r);
    if (
      !Number.isInteger(minutiN) ||
      minutiN < 0 ||
      !Number.isInteger(rpeN) ||
      rpeN < 1 ||
      rpeN > 10
    ) {
      return;
    }
    onSalva(valoreLog(minutiN, rpeN));
  };

  const lettura = meta?.lettura ?? null;
  const IconaLettura = lettura ? ICONA_LETTURA[lettura] : null;

  return (
    <div className="flex items-center gap-1">
      <Input
        aria-label={`${nome} · ${blocco} minuti`}
        placeholder="min"
        inputMode="numeric"
        min={0}
        type="number"
        value={minuti}
        disabled={!scrive}
        className="h-8 w-14 px-1 text-center tabular-nums"
        onChange={(e) => setMinuti(e.target.value)}
        onBlur={conferma}
      />
      <Input
        aria-label={`${nome} · ${blocco} RPE`}
        placeholder="RPE"
        inputMode="numeric"
        min={1}
        max={10}
        type="number"
        value={rpe}
        disabled={!scrive}
        className="h-8 w-12 px-1 text-center tabular-nums"
        onChange={(e) => setRpe(e.target.value)}
        onBlur={conferma}
      />
      <span className="flex w-6 justify-center">
        {esito === 'invio' ? (
          <Spinner className="text-muted-foreground" />
        ) : esito === 'non_salvato' ? (
          <CircleAlertIcon aria-label="Non salvato" className="size-4 text-destructive" />
        ) : (
          lettura &&
          IconaLettura && (
            <span
              role="img"
              aria-label={ETICHETTA_LETTURA[lettura]}
              title={ETICHETTA_LETTURA[lettura]}
              className={cn(
                'flex size-5 items-center justify-center rounded',
                COLORE_LETTURA[lettura],
              )}
            >
              <IconaLettura aria-hidden className="size-3.5" />
            </span>
          )
        )}
      </span>
      {scrive && valore && meta?.motivoVisibile ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label={meta.motivo ? 'Motivo' : 'Aggiungi motivo'}
              className={meta.motivo ? 'text-foreground' : 'text-muted-foreground'}
              onClick={onMotivo}
            >
              {meta.motivo ? <MessageSquareTextIcon /> : <MessageSquarePlusIcon />}
            </Button>
          </TooltipTrigger>
          <TooltipContent>{meta.motivo ?? 'Aggiungi motivo'}</TooltipContent>
        </Tooltip>
      ) : (
        <span className="w-6" />
      )}
    </div>
  );
}

function DialogoMotivo({
  aperto,
  motivo,
  onChiudi,
  onSalva,
}: {
  aperto: boolean;
  motivo: string;
  onChiudi: () => void;
  onSalva: (testo: string) => void;
}) {
  const [testo, setTesto] = useState(motivo);
  return (
    <Dialog open={aperto} onOpenChange={(v) => !v && onChiudi()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Motivo</DialogTitle>
          <DialogDescription>
            Opzionale: perché minuti o RPE sono diversi dal piano.
          </DialogDescription>
        </DialogHeader>
        <Textarea value={testo} onChange={(e) => setTesto(e.target.value)} rows={3} />
        <DialogFooter>
          <Button variant="outline" onClick={onChiudi}>
            Annulla
          </Button>
          <Button onClick={() => onSalva(testo)}>Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
